'use client';

import { Modal } from '@/components/Modal/Modal';
import PlusCircle from '@/components/icons/PlusCircle';
import { NotifsContext } from '@/contexts';
import {
  AdminFormModel,
  useEditEmployeeMutation,
  useGetEmployeeQuery,
  useGetListPdvQuery,
} from '@/services/API';
import { useAppSelector } from '@/store/hooks';
import { ISelectNumeroAgrement, ShortPdv } from '@/types';
import { getURLParams } from '@/utils/router';
import { FC, FormEvent, useContext, useEffect, useState } from 'react';
import Form from '../form';

const Page: FC = () => {
  const siren = useAppSelector((state) => state.selectedSte.societe.siren);
  const searchParams = getURLParams();

  const [admin, setAdmin] = useState<AdminFormModel>();

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const { addToast } = useContext(NotifsContext);

  const {
    data: oldAdmin,
    isLoading: getOldAdminLoading,
    isError: getOldAdminError,
  } = useGetEmployeeQuery({
    siren,
    idEmployee: searchParams.get('id') || '',
  });

  const [editAdmin, { isLoading }] = useEditEmployeeMutation();

  const [pdvs, setPdvs] = useState<ISelectNumeroAgrement[]>([]);
  const {
    data: listPdvs,
    isLoading: getListPdvsLoading,
    isError: getListPdvsError,
  } = useGetListPdvQuery(siren);

  useEffect(() => {
    if (listPdvs && oldAdmin) {
      setAdmin({ ...oldAdmin });
      let prepData: ISelectNumeroAgrement[] = listPdvs?.map(
        ({ bnppf_pos_id, name, partner_pos_id }: ShortPdv) =>
          ({
            bnppf_pos_id,
            name,
            added:
              oldAdmin?.administrative_seller_number_list?.includes(
                bnppf_pos_id
              ),
            partner_pos_id,
          } as ISelectNumeroAgrement)
      );
      setPdvs(prepData);
    }
  }, [listPdvs, oldAdmin]);

  const handleSubmit = (e: FormEvent) => {
    if (admin && oldAdmin) {
      let administrative_seller_number_list: string[] = [];
      if (admin.administrator_type === 'PARTIAL') {
        pdvs.map((pdv) => {
          if (pdv.added)
            administrative_seller_number_list.push(pdv.bnppf_pos_id);
        });
      }
      editAdmin({
        idEmployee: searchParams.get('id') || '',
        siren: siren,
        body: {
          ...oldAdmin,
          ...admin,
          administrative_seller_number_list,
        },
      })
        .unwrap()
        .then(() => {
          addToast &&
            addToast({
              toastType: 'success',
              children: 'Confirmation de modification.',
            });
        })
        .catch((err) => {
          let errorMessage = `Une erreur est survenue.
          Rééssayer ultérieurement.`;
          try {
            if (err.data[0].error === 'uniqueness') {
              errorMessage = `Ce compte existe déjà dans l'annuaire.
              Veuillez vérifier les champs uniques`;
            }
          } catch (error) {}
          addToast &&
            addToast({
              toastType: 'error',
              children: errorMessage,
            });
        })
        .finally(() => setOpenConfirmModal(false));
    }
  };

  return (
    <>
      {!(getOldAdminLoading || getListPdvsLoading) &&
        !(getOldAdminError || getListPdvsError) &&
        !(!admin || !oldAdmin || !listPdvs) && (
          <>
            <Form
              title="Modifier administrateur"
              admin={admin}
              pdvs={pdvs}
              setAdmin={setAdmin}
              setPdvs={setPdvs}
              uid={oldAdmin.uid}
              lastLoginTime={oldAdmin.last_login_time + ''}
              primaryBtnProps={{
                children: 'Modifier un administrateur',
                icon: <PlusCircle />,
                variant: 'primary',
                size: 'medium',
                onClick: () => {
                  setOpenConfirmModal(true);
                },
              }}
            />

            <Modal
              primaryBtnProps={{
                loading: isLoading,
                children: 'Confirmer',
                onClick: handleSubmit,
              }}
              secondaryBtnProps={{
                children: 'Annuler',
                onClick: () => setOpenConfirmModal(false),
              }}
              title="Voulez-vous modifier l'administrateur ?"
              open={openConfirmModal}
              onClose={() => setOpenConfirmModal(false)}
            />
          </>
        )}
    </>
  );
};

export default Page;
